<script setup lang="ts">
useHead({
  title: "Zenapptic AI - Experience Centers",
});

const background = computed(() => {
  return `url(${useCdnUrl()}/assets/solutions/experience-centers/experience-centers-title-image.webp)`;
});
</script>

<template>
  <SolutionsLayout>
    <SolutionsDetailsTitle
      :background-url="`${useCdnUrl()}/assets/solutions/experience-centers/experience-centers-placeholder.webp`"
    >
      <template #videos>
        <source
          :src="`${useCdnUrl()}/assets/solutions/experience-centers/experience-centers-background.webm`"
          type="video/webm"
        />
      </template>

      <template #title><b>EXPERIENCE</b><br />CENTERS</template>

      <template #description>
        Dive into the forefront of innovation with a<br /><b>ZEN3-powered</b> experience, where
        interactive<br />content and mobile/AR/XR technology tell your story
      </template>
    </SolutionsDetailsTitle>

    <div class="feature-content">
      <SolutionsDetailsBlock>
        <template #image>
          <img
            :src="`${useCdnUrl()}/assets/solutions/experience-centers/museums.webp`"
            alt="Museums"
          />
        </template>

        <template #title>Museums & Attractions</template>
        <template #description>
          <p>
            Tell us your storytelling ideas and let the ZEN3 platform transform them into
            unforgettable, immersive experiences that foster a deeper connection with visitors
          </p>
        </template>
        <template #items>
          <ul>
            <li>Interactive Videowalls and Digital Screens</li>
            <li>Personalized Experiences</li>
            <li>Augmented Reality (AR)</li>
          </ul>
        </template>
      </SolutionsDetailsBlock>

      <SolutionsCalloutBlock
        :background-url="`${useCdnUrl()}/assets/solutions/experience-centers/ar-experience.webp`"
      >
        <template #details-left>
          <h2>Add a personalized <b>AR experience</b><br />on smart phones</h2>
          <ul>
            <li>Provide more information with AR overlays</li>
            <li>Add gamification elements</li>
            <li>Include interactive AR maps/wayfinding for better navigation</li>
          </ul>
        </template>

        <template #details-right>
          <VideoPlayer :video-url="`${useCdnUrl()}/assets/video/zen3-explainer-overview.webm`"
            ><img :src="`${useCdnUrl()}/assets/zen3-video-cover.png`"
          /></VideoPlayer>
        </template>
      </SolutionsCalloutBlock>

      <SolutionsDetailsBlock>
        <template #image>
          <img
            :src="`${useCdnUrl()}/assets/solutions/experience-centers/brand-experiences.webp`"
            alt="Museums"
          />
        </template>

        <template #title>Interactive Brand Experiences</template>
        <template #description
          ><p>
            Interactive Brand Experiences involve engaging customers through dynamic and
            participatory content.
          </p>

          <p>
            Unlike traditional advertising, interactive experiences invite users to actively engage
            with the brand.
          </p>

          <p>
            This can include anything from interactive videowalls and immersive virtual reality (VR)
            environments to augmented reality (AR) applications and gamified marketing campaigns.
          </p></template
        >
      </SolutionsDetailsBlock>

      <SolutionsDetailsBlock>
        <template #image>
          <img
            :src="`${useCdnUrl()}/assets/solutions/experience-centers/experience-centers.webp`"
            alt="Museums"
          />
        </template>

        <template #title>Customer<br />Experience Center</template>
        <template #description
          ><p>
            Create carefully curated experiences that convey your brand's values, mission, and
            commitment to delivering exceptional solutions and services.
          </p>

          <p>
            Showcase your products and solutions while fostering collaborative and personalized
            interactions that help build meaningful relationships.
          </p>
        </template>

        <template #items>
          <ul>
            <li>
              <b>Personalization:</b> Showcase products and solutions with dynamic digital displays
              and interactive exhibits that showcase a company's values and capabilities tailored to
              the specific interests and needs of each visitor
            </li>
            <li>
              <b>AR/VR/XR:</b> Use augmented reality (AR), virtual reality (VR), interactive
              displays, and even virtual production stages to create engaging and memorable
              experiences that leave a lasting impression
            </li>
            <li>
              <b>Collaboration:</b> Provide a dedicated space for collaborative discussions,
              workshops, and meetings, and beam in offsite guests to provide live sessions to
              visitors on site
            </li>
            <li>
              <b>Technology Integration:</b> The Zen3 platform integrates with existing systems as
              well as additional technologies such as IoT (Internet of Things) devices,
              location-tracking devices, camera systems, security systems and more
            </li>
          </ul>
        </template>
      </SolutionsDetailsBlock>

      <SolutionsCalloutBlock
        :background-url="`${useCdnUrl()}/assets/solutions/experience-centers/endless-possibilities.webp`"
        style="padding-bottom: 8rem; padding-top: 8rem"
      >
        <template #details-left>
          <h2>
            <b>Endless Possibilities</b><br />to enhance the<br /><b>Customer Experience Center</b
            ><br />of the Future
          </h2>
        </template>
      </SolutionsCalloutBlock>
    </div>

    <ContactBlock />
  </SolutionsLayout>
</template>

<style lang="scss">
.solutions {
  margin-top: 89px;

  .page-feature {
    align-items: center;
    background-image: v-bind(background);
    background-position: center;
    background-size: cover;
    display: flex;
    height: calc(100vh - 89px);
    justify-content: center;
    padding: 2rem;
    width: 100%;

    .feature-details {
      border-left: 1px dotted white;
      display: flex;
      flex-direction: column;
      gap: 5rem;
      max-width: 1100px;
      padding-left: 1rem;
      position: relative;
      width: 100%;

      &:before {
        background-color: #6734bb;
        content: "";
        height: 20px;
        left: -3.5rem;
        position: absolute;
        top: 0;
        width: 20px;
      }

      h1,
      h2 {
        line-height: 1;
        margin: 0;
        padding: 0;
      }

      h2 {
        color: gray;
        font-size: 4rem;
      }

      h1 {
        color: white;
        font-size: 6rem;
        font-weight: 200;
        line-height: 0.9;
      }

      p {
        color: white;
        font-size: 3rem;
        margin: 0;
        padding: 0;
      }
    }
  }

  .feature-content {
    color: white;
    margin: auto;
    max-width: 1366px;
  }
}
</style>
